import { Controller } from "@hotwired/stimulus"

// Allowed media MIME types — mirrors Post::ALLOWED_IMAGE_TYPES /
// ALLOWED_VIDEO_TYPES (model). Client-side check is a UX hint only; the server
// (Post#acceptable_images) stays the authority. `accept=` on the input is
// cosmetic (DevTools-removable), so we re-check here before the upload starts.
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"]
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"]

export default class extends Controller {
  static targets = ["modal", "textarea", "fileInput", "previews", "gateError"]
  // i18n strings injected by the component (chaves EN, traduzidas server-side).
  // Keep all user-facing copy out of the JS source. Limit numbers come from the
  // single source of truth exposed by #767 (data-upload-* attributes), never
  // hardcoded here.
  static values = {
    videoProcessing: { type: String, default: "Processando vídeo…" },
    uploadFailed: { type: String, default: "falha no envio" },
    uploadPending: { type: String, default: "Aguarde o envio terminar." },
    uploadErrorBlock: { type: String, default: "Corrija os arquivos com erro antes de publicar." },
    // Validation messages (already interpolated server-side, except the type
    // message which carries a %{name} placeholder filled with the bad filename).
    errorVideoTooLarge: { type: String, default: "Vídeo muito grande." },
    errorImageTooLarge: { type: String, default: "Imagem muito grande." },
    errorTooManyImages: { type: String, default: "Arquivos demais." },
    errorTotalTooLarge: { type: String, default: "Mídia acima do limite." },
    errorUnsupportedType: { type: String, default: "Tipo de arquivo não suportado: %{name}." }
  }

  // Upload limits come from the data-upload-* attributes emitted by
  // ApplicationHelper#upload_limits_data_attributes (#767) — NOT controller-
  // scoped Stimulus values, so we read them straight from the root dataset.
  // Falls back to safe defaults if (somehow) absent.
  get _limits() {
    const ds = this.element.dataset
    return {
      maxVideoBytes: parseInt(ds.uploadMaxVideoBytes, 10) || Infinity,
      maxImageBytes: parseInt(ds.uploadMaxImageBytes, 10) || Infinity,
      maxImages: parseInt(ds.uploadMaxImages, 10) || 10,
      maxTotalBytes: parseInt(ds.uploadMaxTotalBytes, 10) || Infinity
    }
  }

  connect() {
    this._files = []
    this._uploads = new Map()  // direct-upload id -> overlay element
    this._validationError = false  // client-side selection error blocks submit
    this._handleKeydown = (e) => {
      if (e.key === "Escape") this.close()
    }
    document.addEventListener("keydown", this._handleKeydown)

    this._onUploadInit = this._onUploadInit.bind(this)
    this._onUploadProgress = this._onUploadProgress.bind(this)
    this._onUploadError = this._onUploadError.bind(this)
    this._onUploadEnd = this._onUploadEnd.bind(this)
    this.element.addEventListener("direct-upload:initialize", this._onUploadInit)
    this.element.addEventListener("direct-upload:progress", this._onUploadProgress)
    this.element.addEventListener("direct-upload:error", this._onUploadError)
    this.element.addEventListener("direct-upload:end", this._onUploadEnd)
  }

  disconnect() {
    // If we tear down while portaled (e.g. Turbo navigation with the modal
    // open), put the root back in its original slot first so nothing is stranded
    // on <body> or leaked into Turbo's page cache.
    document.removeEventListener("keydown", this._handleKeydown)
    document.body.style.overflow = ""
    document.body.classList.remove("is-composing")
    this.element.removeEventListener("direct-upload:initialize", this._onUploadInit)
    this.element.removeEventListener("direct-upload:progress", this._onUploadProgress)
    this.element.removeEventListener("direct-upload:error", this._onUploadError)
    this.element.removeEventListener("direct-upload:end", this._onUploadEnd)
    this._uploads.clear()
  }

  // Active Storage direct-upload events: bubbliam do <input type="file"> dentro
  // do form_with submit. Mapeamos detail.id ao preview pre-renderizado por
  // index do _files (file ref ===), e overlayamos uma barra de progresso.
  // Seguranca: filename do file e exibido apenas no <img alt> (ja escapado);
  // nada e injetado via innerHTML.

  _findPreviewForFile(file) {
    const idx = this._files.indexOf(file)
    if (idx < 0) return null
    return this.previewsTarget.children[idx] || null
  }

  _onUploadInit(event) {
    const { id, file } = event.detail
    const previewItem = this._findPreviewForFile(file)
    if (!previewItem) return

    const overlay = document.createElement("div")
    overlay.className = "post-compose-modal__upload-overlay"

    const percent = document.createElement("span")
    percent.className = "post-compose-modal__upload-percent"
    percent.textContent = "0%"

    const bar = document.createElement("div")
    bar.className = "post-compose-modal__upload-bar"

    const fill = document.createElement("div")
    fill.className = "post-compose-modal__upload-fill"
    fill.style.width = "0%"

    bar.appendChild(fill)
    overlay.appendChild(percent)
    overlay.appendChild(bar)
    previewItem.appendChild(overlay)

    // A video's S3 upload finishing is NOT "done" — the backend then transcodes
    // it (#770/#771). We track which uploads are videos so _onUploadEnd can flip
    // the overlay to a "processing video" state instead of removing it.
    const isVideo = file.type.startsWith("video/")
    this._uploads.set(id, { overlay, fill, percent, isVideo })
  }

  _onUploadProgress(event) {
    const { id, progress } = event.detail
    const entry = this._uploads.get(id)
    if (!entry) return
    const pct = Math.max(0, Math.min(100, Math.round(progress)))
    entry.fill.style.width = pct + "%"
    entry.percent.textContent = pct + "%"
  }

  _onUploadError(event) {
    const { id } = event.detail
    event.preventDefault()
    const entry = this._uploads.get(id)
    if (!entry) return
    entry.overlay.classList.add("post-compose-modal__upload-overlay--error")
    entry.failed = true
    entry.percent.textContent = this.uploadFailedValue
  }

  _onUploadEnd(event) {
    const { id } = event.detail
    const entry = this._uploads.get(id)
    if (!entry) return
    if (entry.overlay.classList.contains("post-compose-modal__upload-overlay--error")) return

    if (entry.isVideo) {
      // The bytes reached S3, but the video is not ready: the backend now
      // transcodes it (#770/#771). Keep a clear "Processing video…" badge on the
      // preview so the upload does not look finished/failed. The card in the feed
      // continues this story (poster + spinner) until the ready broadcast.
      entry.overlay.classList.add("post-compose-modal__upload-overlay--processing")
      entry.percent.textContent = this.videoProcessingValue
      entry.fill.style.width = "100%"
      return
    }

    entry.overlay.classList.add("post-compose-modal__upload-overlay--done")
    setTimeout(() => {
      entry.overlay.remove()
      this._uploads.delete(id)
    }, 400)
  }

  open() {
    this.modalTarget.classList.add("post-compose-modal--open")
    document.body.style.overflow = "hidden"
    document.body.classList.add("is-composing")
    this.textareaTarget.focus()
  }

  openWithImage() {
    this.open()
    setTimeout(() => this.fileInputTarget.click(), 100)
  }

  close() {
    this.modalTarget.classList.remove("post-compose-modal--open")
    document.body.style.overflow = ""
    document.body.classList.remove("is-composing")
  }

  // iOS Safari trap: `position: fixed` inside a scroll container that has
  // `-webkit-overflow-scrolling: touch` (our `.app-layout__scroll`) is
  // positioned relative to the CONTAINER, not the viewport — so the fixed modal
  // gets confined between the app header and the bottom-nav (Publicar hides
  // behind the nav, the app chrome shows through). To escape the trap we move
  // the composer OUT of `.app-layout__scroll` and onto <body> while the modal is
  // open, so the fixed element resolves against the viewport again (as it does
  // on desktop).
  //
  // We move the WHOLE controller root (`this.element`), not just the modal node:
  // moving only the modal would take it out of the controller's DOM scope and
  // Stimulus would tear down the modal's actions/targets (close, submit guard,
  // upload preview) and the direct-upload listeners bound on this.element. By
  // relocating the entire subtree in a single DOM op within the same document,
  // every containment relationship is preserved — Stimulus keeps the controller
  // instance, all bindings, and the in-flight upload state intact (no
  // disconnect/connect). A comment placeholder marks the original slot so close()
  // restores it exactly (no duplicated/leaked node). The trigger bar rides along
  // but is hidden behind the full-screen modal while open.
  // Block publishing while an upload has FAILED (#771, merges with #768). A
  // failed direct upload means the blob never reached S3, so submitting would
  // create a post missing that media silently. We do NOT block on a video that
  // finished uploading and is merely transcoding (entry.isVideo without .failed)
  // — that post is valid and the feed card shows the processing/ready state.
  // Server-side validation remains the authority; this is a UX guard only.
  guardSubmit(event) {
    // Block publishing on any of: a client-side selection error still standing
    // (#768), or a direct upload that FAILED (#771). Both must show a VISIBLE
    // message — never a silent abort. A video merely transcoding (isVideo
    // without .failed) is a valid post and does NOT block.
    if (this._validationError) {
      event.preventDefault()
      event.stopPropagation()
      this._showGateError(this.uploadErrorBlockValue)
      return
    }
    const hasFailedUpload = Array.from(this._uploads.values()).some((e) => e.failed)
    if (!hasFailedUpload) {
      this._clearGateError()
      return
    }
    event.preventDefault()
    event.stopPropagation()
    this._showGateError(this.uploadPendingValue)
  }

  _showGateError(message) {
    if (!this.hasGateErrorTarget) return
    this.gateErrorTarget.textContent = message
    this.gateErrorTarget.hidden = false
  }

  _clearGateError() {
    if (!this.hasGateErrorTarget) return
    this.gateErrorTarget.hidden = true
    this.gateErrorTarget.textContent = ""
  }

  onSubmitEnd(event) {
    if (event.detail.success) {
      this.close()
      this.textareaTarget.value = ""
      this.textareaTarget.style.height = "auto"
      this._files = []
      this._uploads.clear()
      this._validationError = false
      this._clearGateError()
      this.syncFileInput()
      this.renderPreviews()
    }
  }

  // Validate each freshly selected file BEFORE it is queued/uploaded, so the
  // user gets an immediate, specific error instead of a silent abort (#768).
  // Order of checks per file: type → per-file size → image count → total size.
  // A rejected file is NOT added; valid files in the same selection still are.
  // Server-side validation remains the authority — this is UX/feedback only.
  previewImages() {
    const limits = this._limits
    const newFiles = Array.from(this.fileInputTarget.files)
    let error = null

    for (const file of newFiles) {
      const isVideo = ALLOWED_VIDEO_TYPES.includes(file.type) || file.type.startsWith("video/")
      const isImage = ALLOWED_IMAGE_TYPES.includes(file.type)

      if (!isImage && !ALLOWED_VIDEO_TYPES.includes(file.type)) {
        error = this._interpolate(this.errorUnsupportedTypeValue, { name: file.name })
        continue
      }
      if (isVideo && file.size > limits.maxVideoBytes) {
        error = this.errorVideoTooLargeValue
        continue
      }
      if (isImage && file.size > limits.maxImageBytes) {
        error = this.errorImageTooLargeValue
        continue
      }
      if (this._files.length >= limits.maxImages) {
        error = this.errorTooManyImagesValue
        continue
      }
      if (this._currentTotalBytes() + file.size > limits.maxTotalBytes) {
        error = this.errorTotalTooLargeValue
        continue
      }
      this._files.push(file)
    }

    if (error) {
      this._validationError = true
      this._showGateError(error)
    } else {
      this._validationError = false
      this._clearGateError()
    }

    this.syncFileInput()
    this.renderPreviews()
  }

  _currentTotalBytes() {
    return this._files.reduce((sum, f) => sum + f.size, 0)
  }

  // Tiny %{name} interpolation — keeps the placeholder convention identical to
  // the Rails i18n string so the message reads naturally in both locales.
  _interpolate(template, vars) {
    return template.replace(/%\{(\w+)\}/g, (_, key) => (key in vars ? vars[key] : `%{${key}}`))
  }

  removeImage(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    this._files.splice(index, 1)
    // Removing a file may resolve the over-limit condition; re-clear the gate
    // so the user is not stuck behind a stale error.
    this._validationError = false
    this._clearGateError()
    this.syncFileInput()
    this.renderPreviews()
  }

  syncFileInput() {
    const dt = new DataTransfer()
    this._files.forEach((f) => dt.items.add(f))
    this.fileInputTarget.files = dt.files
  }

  renderPreviews() {
    // Revoke previous object URLs
    this.previewsTarget.querySelectorAll("img, video").forEach((el) => {
      if (el.src && el.src.startsWith("blob:")) URL.revokeObjectURL(el.src)
    })

    const container = this.previewsTarget
    container.innerHTML = ""
    const count = this._files.length

    if (count === 0) {
      container.removeAttribute("data-count")
      return
    }

    container.setAttribute("data-count", count)

    this._files.forEach((file, index) => {
      const item = document.createElement("div")
      item.className = "post-compose-modal__preview-item"

      let media
      if (file.type.startsWith("video/")) {
        media = document.createElement("video")
        media.className = "post-compose-modal__preview-img"
        media.src = URL.createObjectURL(file)
        media.controls = true
        media.playsInline = true
        media.preload = "metadata"
      } else {
        media = document.createElement("img")
        media.className = "post-compose-modal__preview-img"
        media.src = URL.createObjectURL(file)
      }
      const img = media

      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "post-compose-modal__preview-remove"
      btn.dataset.index = index
      btn.dataset.action = "post-compose#removeImage"
      btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'

      item.appendChild(img)
      item.appendChild(btn)
      container.appendChild(item)
    })
  }

  autoResize() {
    const textarea = this.textareaTarget
    textarea.style.height = "auto"
    textarea.style.height = textarea.scrollHeight + "px"
  }

}
